export type DisplayPricing = {
  price: number;
  salePrice?: number;
  compareAtPrice?: number;
};

export function coerceMoney(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (
    typeof value === "string" &&
    value.trim() !== "" &&
    Number.isFinite(Number(value))
  )
    return Number(value);
  return undefined;
}

function isSalePrice(
  value: number | undefined,
  regularPrice: number,
): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= regularPrice
  );
}

export function resolveDisplayPricing(
  regularPriceValue: unknown,
  salePriceValue?: unknown,
  compareAtPriceValue?: unknown,
  fallbackPrice = 0,
): DisplayPricing {
  const explicitSalePrice = coerceMoney(salePriceValue);
  const compareAtCandidate = coerceMoney(compareAtPriceValue);
  const explicitRegularPrice = coerceMoney(regularPriceValue);
  const regularPrice =
    explicitRegularPrice ??
    (isSalePrice(explicitSalePrice, compareAtCandidate ?? Number.NaN)
      ? compareAtCandidate
      : undefined) ??
    (typeof explicitSalePrice === "number" && fallbackPrice <= 0
      ? explicitSalePrice
      : undefined) ??
    fallbackPrice;

  let price = regularPrice;
  let salePrice: number | undefined;
  let compareAtPrice: number | undefined;

  if (isSalePrice(explicitSalePrice, regularPrice)) {
    salePrice = explicitSalePrice;
    price = explicitSalePrice;
    if (explicitSalePrice < regularPrice) {
      compareAtPrice = regularPrice;
    }
  }

  if (typeof compareAtCandidate === "number") {
    if (compareAtCandidate > price) {
      compareAtPrice = Math.max(compareAtPrice ?? 0, compareAtCandidate);
    } else if (
      salePrice === undefined &&
      isSalePrice(compareAtCandidate, regularPrice)
    ) {
      salePrice = compareAtCandidate;
      price = compareAtCandidate;
      if (compareAtCandidate < regularPrice) {
        compareAtPrice = regularPrice;
      }
    }
  }

  return {
    price,
    ...(salePrice !== undefined ? { salePrice } : {}),
    ...(compareAtPrice !== undefined ? { compareAtPrice } : {}),
  };
}

export function resolveListedProductPricing(product: {
  price?: number | null;
  salePrice?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
  compareAt?: number | null;
  compareAtPrice?: number | null;
  compareAtMax?: number | null;
}): DisplayPricing {
  const price = coerceMoney(product.price);
  const salePrice = coerceMoney(product.salePrice);
  const priceMin = coerceMoney(product.priceMin);
  const priceMax = coerceMoney(product.priceMax);
  const compareAt = coerceMoney(
    product.compareAtPrice ?? product.compareAt ?? product.compareAtMax,
  );
  const effectivePrice = salePrice ?? priceMin ?? price ?? 0;
  const regularPrice =
    price ??
    (salePrice !== undefined && priceMax !== undefined && priceMax >= salePrice
      ? priceMax
      : undefined) ??
    (salePrice !== undefined && priceMin !== undefined && priceMin >= salePrice
      ? priceMin
      : undefined) ??
    priceMax ??
    compareAt ??
    effectivePrice;

  if (salePrice === undefined) {
    return compareAt !== undefined
      ? resolveDisplayPricing(
          effectivePrice,
          undefined,
          compareAt,
          effectivePrice,
        )
      : { price: effectivePrice };
  }

  return resolveDisplayPricing(
    regularPrice,
    salePrice,
    compareAt,
    effectivePrice,
  );
}
